import React, { useState, useEffect } from 'react';
import { ChevronDown, X, Menu } from 'lucide-react';

const Header = () => {
  const [showArchiveDropdown, setShowArchiveDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileArchiveOpen, setMobileArchiveOpen] = useState(false);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const menuItems = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Team', href: '/team' },
    { name: 'Submission', href: 'https://filmfreeway.com/GuwahatiAsianFilmFestival', external: true },
    { name: 'Rules', href: '/rules' },
    { name: 'Archive', dropdown: true },
    { name: 'Gallery', href: '/#gallery' },
    { name: 'Contact', href: '/contact' },
  ];

  const archiveItems = [
    { name: 'GAFF 2026', href: '/gaff-2026' },
    { name: 'GAFF 2025', href: '/gaff-2025' }
  ];

  const handleMenuItemClick = () => {
    setMobileMenuOpen(false);
    setMobileArchiveOpen(false);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-white/5">
      <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="hover:opacity-80 transition-opacity z-50" onClick={() => setMobileMenuOpen(false)}>
            <img
              src="https://customer-assets.emergentagent.com/job_homepage-redesign-26/artifacts/k6lh32mr_logo-20.png"
              alt="GAFF"
              className="h-16 sm:h-18 md:h-20 w-auto" />
          </a>

          {/* Navigation Menu - Desktop */}
          <nav className="hidden lg:flex items-center gap-8">
            {menuItems.map((item, index) => {
              if (item.dropdown) {
                return (
                  <div
                    key={index}
                    className="relative group"
                    onMouseEnter={() => setShowArchiveDropdown(true)}
                    onMouseLeave={() => setShowArchiveDropdown(false)}
                  >
                    <button className="text-sm uppercase tracking-widest text-white hover:text-red-500 transition-colors duration-300 relative flex items-center gap-1 py-2">
                      {item.name}
                      <ChevronDown size={16} className={`transition-transform duration-200 ${showArchiveDropdown ? 'rotate-180' : ''}`} />
                      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-red-500 group-hover:w-full transition-all duration-300"></span>
                    </button>
                    
                    {/* Dropdown Menu */}
                    {showArchiveDropdown && (
                      <div className="absolute top-full left-0 pt-2 w-48">
                        <div className="bg-black/95 backdrop-blur-sm border border-white/10 rounded-lg shadow-xl overflow-hidden">
                          {archiveItems.map((archiveItem, idx) => (
                            <a
                              key={idx}
                              href={archiveItem.href}
                              className="block px-6 py-3 text-sm uppercase tracking-widest text-white hover:bg-red-500 hover:text-white transition-all duration-200"
                            >
                              {archiveItem.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <a
                  key={index}
                  href={item.href}
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noopener noreferrer" : undefined}
                  className="text-sm uppercase tracking-widest text-white hover:text-red-500 transition-colors duration-300 relative group"
                >
                  {item.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-red-500 group-hover:w-full transition-all duration-300"></span>
                </a>
              );
            })}
          </nav>

          {/* Mobile Menu Button - Hamburger */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-white z-[60] relative p-2 -mr-2 touch-manipulation active:scale-95 transition-transform"
            aria-label="Toggle menu"
          >
            <div className="relative w-6 h-6 flex items-center justify-center">
              {/* Animated Hamburger to X */}
              <div className="relative flex flex-col items-center justify-center w-full h-full">
                <span className={`absolute h-0.5 w-6 bg-white transform transition-all duration-300 ease-in-out ${
                  mobileMenuOpen ? 'rotate-45 translate-y-0' : '-translate-y-2'
                }`}></span>
                <span className={`absolute h-0.5 w-6 bg-white transform transition-all duration-300 ease-in-out ${
                  mobileMenuOpen ? 'opacity-0 scale-0' : 'opacity-100 scale-100'
                }`}></span>
                <span className={`absolute h-0.5 w-6 bg-white transform transition-all duration-300 ease-in-out ${
                  mobileMenuOpen ? '-rotate-45 translate-y-0' : 'translate-y-2'
                }`}></span>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay - Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/80 backdrop-blur-sm z-[55] lg:hidden transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
      />

      {/* Mobile Menu Drawer - Sliding from Right */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-[85vw] max-w-[320px] bg-black border-l border-white/10 shadow-2xl z-[60] transform transition-all duration-300 ease-out lg:hidden ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Drawer Header */}
        <div className="sticky top-0 bg-black border-b border-white/10 px-6 py-5 flex items-center justify-end">
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 -mr-2 text-white/70 hover:text-white transition-colors touch-manipulation active:scale-95"
            aria-label="Close menu"
          >
            <X size={24} />
          </button>
        </div>

        {/* Drawer Content - Scrollable */}
        <div className="h-[calc(100vh-73px)] overflow-y-auto overscroll-contain bg-black">
          <nav className="px-6 py-6 bg-black">
            <ul className="space-y-1">
              {menuItems.map((item, index) => {
                if (item.dropdown) {
                  return (
                    <li key={index} className="border-b border-white/5 last:border-0">
                      <button
                        onClick={() => setMobileArchiveOpen(!mobileArchiveOpen)}
                        className="w-full flex items-center justify-between py-4 text-white hover:text-red-500 active:text-red-500 transition-colors touch-manipulation group bg-black"
                      >
                        <span className="text-base uppercase tracking-wide font-semibold">{item.name}</span>
                        <ChevronDown 
                          size={20} 
                          className={`transition-transform duration-300 text-red-500 ${mobileArchiveOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {/* Submenu with smooth height transition */}
                      <div className={`overflow-hidden transition-all duration-300 ease-in-out bg-black ${
                        mobileArchiveOpen ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                      }`}>
                        <div className="pb-3 pl-4 space-y-1 bg-black">
                          {archiveItems.map((archiveItem, idx) => (
                            <a
                              key={idx}
                              href={archiveItem.href}
                              onClick={handleMenuItemClick}
                              className="block py-3 px-4 text-sm text-white/80 hover:text-red-500 rounded-lg transition-all uppercase tracking-wide touch-manipulation bg-black"
                            >
                              <span className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                                {archiveItem.name}
                              </span>
                            </a>
                          ))}
                        </div>
                      </div>
                    </li>
                  );
                }

                return (
                  <li key={index} className="border-b border-white/5 last:border-0">
                    <a
                      href={item.href}
                      target={item.external ? "_blank" : undefined}
                      rel={item.external ? "noopener noreferrer" : undefined}
                      onClick={handleMenuItemClick}
                      className="flex items-center justify-between py-4 text-base uppercase tracking-wide font-semibold text-white hover:text-red-500 active:text-red-500 transition-colors touch-manipulation group bg-black"
                    >
                      <span>{item.name}</span>
                      <span className="w-0 group-hover:w-2 h-2 rounded-full bg-red-500 transition-all duration-300"></span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;